import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectActiveRestaurantCode,
  selectShowTaskRouterOnBanner,
} from '../../selectors/restaurant';

// Shares RefreshBanner's floating-card geometry so app-level notices read as
// one family. Amber accent (rather than RefreshBanner's blue/red actions)
// because this is an advisory, not something the agent must act on.
const useStyles = makeStyles<Theme>(({ spacing }) => ({
  banner: {
    position: 'absolute',
    top: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: '640px',
    width: 'fit-content',
    background: '#02263d',
    color: '#fff',
    borderLeft: '4px solid #FFC107',
    paddingTop: spacing(1.5),
    paddingBottom: spacing(1.5),
    paddingLeft: '20px',
    paddingRight: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    zIndex: 9999,
  },
  icon: {
    color: '#FFC107',
    flexShrink: 0,
  },
  message: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
  dismissButton: {
    background: '#FFC107',
    border: 'none',
    padding: `${spacing(1)} ${spacing(1.5)}`,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background-color 0.2s, transform 0.2s, box-shadow 0.2s',
    color: 'rgba(0,0,0,0.87)',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    '&:hover': {
      background: '#FFD54F',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(255, 193, 7, 0.4)',
    },
    '&:active': {
      background: '#FFB300',
      transform: 'scale(0.98)',
    },
  },
}));

/**
 * HITL-1459: warns an agent who reached a task-router-enabled restaurant by
 * picking it from the dropdown that an order here belongs to whoever the task
 * router assigns it to. Advisory only — nothing on the page is disabled.
 */
const TaskRouterOnBanner = () => {
  const { banner, icon, message, dismissButton } = useStyles();
  const showBanner = useAppSelector(selectShowTaskRouterOnBanner);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const sessionId = useAppSelector(selectSessionId);
  // Remember WHICH restaurant was dismissed, not merely that a dismissal
  // happened — switching to a different task-router store is a new warning.
  const [dismissedCode, setDismissedCode] = useState('');

  // Drop the acknowledgement on every new car (sessionId changes on car_enter)
  // and whenever the advisory stops applying. A dismissal only ever covers the
  // order in front of the agent: the next car is a fresh chance to step on an
  // order the task router assigned to somebody else, which is the whole point
  // of the warning, so it has to be raised again.
  useEffect(() => {
    setDismissedCode('');
  }, [sessionId, showBanner]);

  if (!showBanner || dismissedCode === restaurantCode) return null;

  return (
    // data-task-router-banner keeps this out of agent-interception detection
    // (AGENT_INTERCEPTION_IGNORED_SELECTORS). Both guards listen on document in
    // the capture phase, so stopping the event on the button would not work —
    // and acknowledging a "don't touch this order" warning must never be read
    // as taking the order over.
    <div
      className={banner}
      data-task-router-banner="true"
      data-testid="task-router-on-banner"
    >
      <WarningAmberIcon className={icon} />
      <span className={message}>
        Task Router is ON for this restaurant — do not take orders here.
      </span>
      <button
        className={dismissButton}
        onClick={() => setDismissedCode(restaurantCode)}
      >
        Got it
      </button>
    </div>
  );
};

export default TaskRouterOnBanner;
